import { dayjs } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { ContractServices } from 'network'
import { useBaseInfoStore, useDoubleContractAssetsAccountStore, useExchangeInfoStore, useResponsive, useUserCenterContractOrderStore } from 'store'
import { useLoadMore } from 'store/src/stores/use-load-more'
import { DefineComponentProps, safeSecondToMillisecond } from 'utils'
import { LoadMore, VirtualList, VirtualTable } from '..'
import type { TableColumnProps } from '../components/VirtualTable/VirtualTableThCell'
import { OrderListLayout } from '../layouts/Order.layout'
import { FundFlowItem } from './FundFlowItem'

export type FundFlowProps = {
  positionId?: string
  symbolId?: SymbolId
}

export const FundFlow = defineComponent<FundFlowProps>({
  props: DefineComponentProps<FundFlowProps>(['positionId', 'symbolId']),
  setup(props) {
    const contractOrderListStore = useUserCenterContractOrderStore()
    const exchangeStore = useExchangeInfoStore()
    const { t } = useI18n()
    const responsive = useResponsive()
    const baseInfoStore = useBaseInfoStore()
    const doubleAccount = useDoubleContractAssetsAccountStore()

    const fundFlowStore = useLoadMore<ApiResponse.FundFlow>({
      isInitFetch: false,
      requestInterceptor: () => {
        return doubleAccount.openContractFeature === '1'
      },
      fetcher: props.positionId ? ContractServices.getFundFlowInPosition.bind(ContractServices) : ContractServices.getFundFlow.bind(ContractServices),
      handleParams: (params, dataSource) => {
        const ajId = dataSource?.[dataSource.length - 1]?.ajId
        return { ...(params || {}), positionId: props.positionId, ajId }
      },
    })

    watch(
      () => doubleAccount.doubleContractAssetsAccount?.openFutures,
      (openFutures) => {
        if (openFutures) {
          fundFlowStore.initFetch()
        }
      },
      {
        immediate: true,
      },
    )

    const columns = computed<TableColumnProps<ApiResponse.FundFlow>[]>(() => {
      return [
        {
          label: t('币种'),
          prop: 'symbolId',
          render: (data) => {
            const row = data.row
            const item = baseInfoStore.getCoinInfoByItemId(row.settleItemId)
            const icon = item?.itemDetail.coinUrl || ''
            const itemName = item?.itemName.toUpperCase() || ''

            return (
              <div class={'flex align-center gap-row-8'}>
                <img width={24} src={icon} alt="" />
                <div class={'symbol-info'}>
                  <span class="symbol-name">{itemName}</span>
                </div>
              </div>
            )
          },
        },
        {
          label: t('类型'),
          prop: 'originType',
          render: (data) => {
            return contractOrderListStore.getFundFlowType(data.row)
          },
        },
        {
          label: t('合约'),
          prop: 'symbolId',
          render: (data) => {
            const row = data.row
            const config = exchangeStore.getContractSymbolInfoBySymbolId(row.symbolId)
            if (row.originType === 10003 || row.originType === 10004 || !config?.symbolName) {
              return '--'
            }
            return (
              <span>
                {exchangeStore.getContractSymbolName(row.symbolId)} {t('永续')}
              </span>
            )
          },
        },
        {
          label: t('总额'),
          prop: 'delta',
          render: (data) => {
            return (+data.row.delta >= 0 ? '+' : '') + data.row.delta.formatNumberWithCommas()
          },
        },
        {
          label: t('时间'),
          prop: 'createTime',
          render: (data) => {
            return dayjs(safeSecondToMillisecond(data.row.createTime)).format('YYYY-MM-DD HH:mm:ss')
          },
        },
      ] as TableColumnProps<ApiResponse.FundFlow>[]
    })

    return () => {
      if (responsive.isMobile) {
        return (
          <LoadMore loading={fundFlowStore.loadMoreLoading.value} noMore={!fundFlowStore.hasMore.value} onLoadMore={fundFlowStore.loadMore}>
            <VirtualList itemMinHeight={155} data={fundFlowStore.dataSource.value} noRenderWhenNotInView={false}>
              {{
                default: ({ data, index }: { data: ApiResponse.FundFlow; index: number }) => {
                  return <FundFlowItem data={data} divider={index !== fundFlowStore.dataSource.value.length - 1} />
                },
              }}
            </VirtualList>
          </LoadMore>
        )
      }
      return (
        <OrderListLayout style={{ padding: 0 }}>
          <LoadMore loading={fundFlowStore.loadMoreLoading.value} noMore={!fundFlowStore.hasMore.value} onLoadMore={fundFlowStore.loadMore}>
            <VirtualTable rowHeight={60} data={fundFlowStore.dataSource.value} columns={columns.value} />
          </LoadMore>
        </OrderListLayout>
      )
    }
  },
})
